/**
 * Блок «Площадка сегодня» на обзоре карточки проекта (ПР-12, ПФ-06):
 * краткая сводка дня со ссылкой на полный экран.
 */
import { can, type ProjectDetail, type SiteDaySummary } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api } from '../../lib/api';
import { todayLocal } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Badge, ErrorBox, Spinner } from '../../ui/feedback';
import { Panel } from '../../ui/layout';

export function SiteDayPanel({
  project,
  target,
}: {
  project: ProjectDetail;
  target: {
    legalEntityId: string;
    directionId: string | null;
    projectId: string;
    projectPath: Array<string | null>;
  };
}) {
  const me = useMe();
  const day = todayLocal();
  const sees =
    Boolean(me.tenant?.staff) &&
    ['site.view', 'site.report', 'site.review'].some((p) => can(me.access, p, target));
  const q = useQuery({
    queryKey: ['site', 'day-summary', project.id, day],
    queryFn: () => api<SiteDaySummary>('/site/day-summary', { query: { projectId: project.id, date: day } }),
    enabled: sees,
  });
  if (!sees) return null;

  const d = q.data;
  const cells = d
    ? [
        { label: 'Отчёты', value: String(d.reports.length) },
        {
          label: 'Люди',
          value: String(d.labor.reduce((n, x) => n + x.headcount, 0)),
        },
        { label: 'Техника', value: String(d.machines.length) },
        { label: 'Фото', value: String(d.photoCount) },
        ...(d.videoCount ? [{ label: 'Видео', value: String(d.videoCount) }] : []),
        ...(d.remarks ? [{ label: 'Замечания', value: String(d.remarks.length) }] : []),
        ...(d.missing.length ? [{ label: 'Не сдали', value: String(d.missing.length), warn: true }] : []),
        ...(d.delays.length ? [{ label: 'Отставания', value: String(d.delays.length), warn: true }] : []),
      ]
    : [];

  return (
    <Panel
      title="Площадка сегодня"
      meta={day.split('-').reverse().join('.')}
      actions={
        <Link
          to="/site/summary"
          search={{ project: project.id, day } as never}
          className="inline-flex min-h-11 items-center rounded-sm px-3 text-[14px] text-signal underline-offset-2 hover:underline"
        >
          Сводка дня
        </Link>
      }
    >
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : d ? (
        <div className="flex flex-col gap-4">
          <p className="text-[14px] leading-5 text-ink">{d.text}</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {cells.map((c) => (
              <div key={c.label} className="rounded-md border border-line px-3 py-2">
                <div className="text-[12px] text-ink-2">{c.label}</div>
                <div className={`num text-[20px] font-semibold ${c.warn ? 'text-danger' : 'text-ink'}`}>
                  {c.value}
                </div>
              </div>
            ))}
          </div>
          {d.missing.length || d.delays.length || d.remarks?.some((r) => r.stopWork) ? (
            <div className="flex flex-wrap gap-2">
              {d.missing.length ? <Badge tone="warning">Не сдали отчёт: {d.missing.length}</Badge> : null}
              {d.delays.length ? <Badge tone="warning">Отставания: {d.delays.length}</Badge> : null}
              {d.remarks?.some((r) => r.stopWork) ? <Badge tone="danger">Есть остановка работ</Badge> : null}
            </div>
          ) : null}
          <div className="flex flex-wrap gap-3">
            <Link
              to="/site/day"
              search={{ day } as never}
              className="inline-flex min-h-11 items-center text-[14px] text-signal underline-offset-2 hover:underline"
            >
              Мой день
            </Link>
            <Link
              to="/site"
              search={{ project: project.id, day } as never}
              className="inline-flex min-h-11 items-center text-[14px] text-signal underline-offset-2 hover:underline"
            >
              Отчёт смены
            </Link>
          </div>
        </div>
      ) : null}
    </Panel>
  );
}
